import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'br', 'hr',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'ol', 'ul', 'li',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'blockquote', 'pre', 'code',
  'figure', 'figcaption',
  'dl', 'dt', 'dd', 'abbr', 'address',
  'section', 'article', 'header', 'footer', 'nav', 'main', 'aside',
  'details', 'summary', 'mark', 'small', 'del', 'ins', 'wbr',
  'picture', 'source', 'video', 'audio',
];

const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height',
  'colspan', 'rowspan', 'class', 'style', 'id', 'name',
  'align', 'valign', 'border', 'cellpadding', 'cellspacing',
  'target', 'rel', 'type',
  'controls', 'autoplay', 'loop', 'muted', 'poster',
  'loading', 'decoding', 'srcset', 'sizes', 'media',
  'lang', 'dir', 'role',
];

const FORBID_TAGS = [
  'script', 'iframe', 'object', 'embed',
  'form', 'input', 'textarea', 'select', 'button',
  'style', 'link', 'base', 'meta', 'noscript',
  'math', 'svg',
];

const FORBID_ATTR = ['srcdoc', 'formaction', 'action', 'ping'];

const ALLOWED_URI_REGEXP = /^(?!javascript:|vbscript:|data:(?!image\/(?:png|jpe?g|gif|webp|svg\+xml|tiff?);base64,))/i;

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName.startsWith('on')) {
    data.keepAttr = false;
  }
});

export function sanitizeDocHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOWED_URI_REGEXP,
    ALLOW_ARIA_ATTR: true,
    ALLOW_DATA_ATTR: false,
  });
}

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch]);
}

/**
 * Derives the lookup key for a custom field shortcode from its display name.
 *
 * Mirrors how the backend builds the shortcode in
 * OrgCustomFieldServiceImpl.java:73 —
 *   internalName = '_' + name.replaceAll("[\\W]|_", "_") + '_'
 * Every non-alphanumeric is *mapped to* an underscore (not deleted), so
 * "Visitor's Initials" yields the token {{_Visitor_s_Initials_}}.
 *
 * Java's default \W and JS's \W are both ASCII-only, so this maps 1:1.
 * Leading/trailing underscores are trimmed here because the token lookup in
 * signDocument.tsx trims them too, which preserves the {{_*Name_*}} padding
 * semantics for names that start or end with a special character.
 */
export function customFieldKey(name: string): string {
  return name.replace(/[\W]|_/g, "_").replace(/^_+|_+$/g, "");
}

const PLACEHOLDER_RE = /\{\{[\w.-]+\}\}/g;

export function renderDocTemplate(
  rawHtml: string,
  placeholders: Record<string, string>,
): string {
  const html = `<div style="width:100%">${rawHtml}</div>`;
  const substituted = html.replace(PLACEHOLDER_RE, (token) =>
    Object.hasOwn(placeholders, token) ? escapeHtml(placeholders[token]) : token,
  );
  return sanitizeDocHtml(substituted);
}
